<template>
  <div class="video-player-container">
    <!-- Placeholder when no video source -->
    <div v-if="!videoSrc || videoSrc === ''" class="video-placeholder">
      <div class="placeholder-content">
        <i class="bi bi-play-circle placeholder-icon"></i>
        <p class="placeholder-text">Video content will be available soon</p>
      </div>
    </div>

    <!-- YouTube iframe embed -->
    <div v-else-if="isYouTubeVideo" class="youtube-wrapper">
      <iframe
        :src="youtubeEmbedUrl"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        class="youtube-iframe"
      ></iframe>
    </div>

    <!-- Video poster/thumbnail with play button -->
    <div v-else-if="!isPlaying" class="video-poster" @click="playVideo">
      <video
        ref="videoElement"
        class="video-thumbnail"
        :src="videoSrc"
        muted
      ></video>
      <div class="play-button-overlay">
        <button class="play-button" aria-label="Play video">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="32" fill="url(#gradient)" class="circle-bg"/>
            <path d="M26 20L44 32L26 44V20Z" fill="white"/>
            <defs>
              <linearGradient id="gradient" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                <stop stop-color="#9C27B0"/>
                <stop offset="1" stop-color="#E91E63"/>
              </linearGradient>
            </defs>
          </svg>
        </button>
      </div>
    </div>

    <!-- Video player with controls -->
    <div v-else class="video-player-wrapper">
      <video
        ref="videoElement"
        class="video-element"
        :src="videoSrc"
        controls
        muted
        autoplay
        @ended="onVideoEnded"
        @click.stop
      ></video>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoPlayer',
  props: {
    videoSrc: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isPlaying: false
    }
  },
  computed: {
    isYouTubeVideo() {
      if (!this.videoSrc) return false
      return this.videoSrc.includes('youtube.com') ||
             this.videoSrc.includes('youtu.be')
    },
    youtubeEmbedUrl() {
      if (!this.isYouTubeVideo) return ''

      let videoId = ''

      // Extract video ID from various YouTube URL formats
      if (this.videoSrc.includes('youtu.be/')) {
        // Format: https://youtu.be/VIDEO_ID
        videoId = this.videoSrc.split('youtu.be/')[1].split('?')[0]
      } else if (this.videoSrc.includes('youtube.com/watch')) {
        // Format: https://www.youtube.com/watch?v=VIDEO_ID
        const urlParams = new URLSearchParams(this.videoSrc.split('?')[1])
        videoId = urlParams.get('v')
      } else if (this.videoSrc.includes('youtube.com/embed/')) {
        // Already in embed format
        return this.videoSrc
      }

      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0`
    }
  },
  methods: {
    playVideo() {
      this.isPlaying = true
      this.$nextTick(() => {
        const video = this.$refs.videoElement
        if (video) {
          video.play()
        }
      })
    },
    onVideoEnded() {
      this.isPlaying = false
      const video = this.$refs.videoElement
      if (video) {
        video.currentTime = 0
      }
    }
  }
}
</script>

<style scoped>
.video-player-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 300px;
  background: #fff;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.video-placeholder {
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.placeholder-content {
  text-align: center;
  padding: 2rem;
}

.placeholder-icon {
  font-size: 4rem;
  color: #9C27B0;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.placeholder-text {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.youtube-wrapper {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
  border-radius: 0.75rem;
}

.youtube-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 0.75rem;
}

.video-poster {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-button-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.play-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.play-button:hover {
  transform: scale(1.1);
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.2));
}

.play-button:active {
  transform: scale(1.05);
}

.circle-bg {
  transition: all 0.3s ease;
}

.video-player-wrapper {
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .video-player-container {
    min-height: 200px;
  }

  .video-player-wrapper {
    min-height: 200px;
  }

  .play-button svg {
    width: 48px;
    height: 48px;
  }
}
</style>
